import type { MediaCaptureService, CaptureSessionSnapshot, CaptureSessionListener } from '../ports';
import type { CaptureSettings, CaptureSessionState, CaptureError } from '@shared/types';
import { toCaptureError } from './captureErrors';

/**
 * navigator.mediaDevices-backed implementation of MediaCaptureService.
 *
 * Responsibility is intentionally narrow: acquire/release a MediaStream
 * that matches the requested CaptureSettings, and notify listeners of
 * state transitions. It does NOT know about React, Zustand, encoding,
 * or any downstream consumer — those are composed on top via the
 * useMediaCapture hook and mediaCaptureStore.
 */
export class WebRtcMediaCaptureService implements MediaCaptureService {
  private stream: MediaStream | null = null;
  private state: CaptureSessionState = 'idle';
  private settings: CaptureSettings;
  private error: CaptureError | null = null;
  private listeners = new Set<CaptureSessionListener>();
  private startPromise: Promise<void> | null = null;

  constructor(initialSettings: CaptureSettings) {
    this.settings = initialSettings;
  }

  async start(settings: CaptureSettings): Promise<void> {
    console.log('[MediaCapture] start() called, current state:', this.state, 'startPromise exists:', !!this.startPromise);

    if (this.startPromise) {
      console.log('[MediaCapture] returning existing in-flight promise');
      return this.startPromise;
    }

    this.settings = settings;
    this.setState('requesting-permissions');

    this.startPromise = (async () => {
      try {
        console.log('[MediaCapture] calling getUserMedia now');
        this.stopTracks();
        this.stream = await navigator.mediaDevices.getUserMedia(this.buildConstraints(settings));
        console.log('[MediaCapture] getUserMedia SUCCESS');
        this.error = null;
        this.setState('active');
      } catch (cause) {
        console.log('[MediaCapture] getUserMedia FAILED', cause);
        this.error = toCaptureError(cause);
        this.setState('error');
        throw this.error;
      } finally {
        this.startPromise = null;
      }
    })();

    return this.startPromise;
  }

  async stop(): Promise<void> {
    this.stopTracks();
    this.setState('stopped');
  }

  async updateSettings(partial: Partial<CaptureSettings>): Promise<void> {
    const next = { ...this.settings, ...partial };
    if (this.state === 'active') {
      // Re-acquire with new constraints. A future optimization could use
      // MediaStreamTrack.applyConstraints() for in-place updates.
      await this.start(next);
    } else {
      this.settings = next;
      this.emit();
    }
  }

  getSnapshot(): CaptureSessionSnapshot {
    return { state: this.state, settings: this.settings, error: this.error };
  }

  subscribe(listener: CaptureSessionListener): () => void {
    this.listeners.add(listener);
    listener(this.getSnapshot());
    return () => this.listeners.delete(listener);
  }

  getOutputStream(): MediaStream | null {
    return this.stream;
  }

  private setState(state: CaptureSessionState): void {
    this.state = state;
    this.emit();
  }

  private emit(): void {
    const snapshot = this.getSnapshot();
    this.listeners.forEach((listener) => listener(snapshot));
  }

  private stopTracks(): void {
    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = null;
  }

  private buildConstraints(settings: CaptureSettings): MediaStreamConstraints {
    return {
      video: settings.videoDeviceId
        ? {
            deviceId: { exact: settings.videoDeviceId },
            width: { ideal: settings.resolution.width },
            height: { ideal: settings.resolution.height },
            frameRate: { ideal: settings.frameRate },
          }
        : {
            width: { ideal: settings.resolution.width },
            height: { ideal: settings.resolution.height },
            frameRate: { ideal: settings.frameRate },
          },
      audio: settings.audioDeviceId ? { deviceId: { exact: settings.audioDeviceId } } : true,
    };
  }
}
